import { useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Calendar, Clock, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timeSlots = [
  '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', 
  '15:00', '16:00', '17:00', '18:00', '19:00', '20:00'
];

const RequestAppointmentButton = ({ artistId, artistName }) => {
  const { user } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    preferred_date: '',
    preferred_time: '',
    description: ''
  });

  if (user?.user_type !== 'client' || user?.id === artistId) {
    return null;
  }

  const handleSubmit = async () => {
    if (!formData.preferred_date || !formData.preferred_time) {
      toast.error('Por favor selecciona fecha y hora');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/appointment-requests`, {
        artist_id: artistId,
        ...formData,
        reference_images: []
      });
      
      setDialogOpen(false);
      setFormData({ preferred_date: '', preferred_time: '', description: '' });
      toast.success('¡Solicitud enviada! El artista te responderá pronto.');
    } catch (error) {
      console.error('Error creating request:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar la solicitud');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogTrigger asChild>
        <Button 
          className="bg-zinc-100 text-zinc-950 hover:bg-white uppercase tracking-widest text-xs font-bold"
          data-testid="request-appointment-btn"
        >
          <Calendar size={16} className="mr-2" />
          Solicitar Cita
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-['Cinzel'] text-xl">
            Solicitar Cita con {artistName}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <p className="text-sm text-zinc-500">
            Envía una solicitud de cita al artista. Te contactará para confirmar los detalles.
          </p>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Fecha preferida *</Label>
              <Input
                type="date"
                value={formData.preferred_date}
                onChange={(e) => setFormData(prev => ({ ...prev, preferred_date: e.target.value }))}
                className="bg-transparent border-zinc-800 mt-2"
                min={new Date().toISOString().split('T')[0]}
                data-testid="request-date"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Hora preferida *</Label>
              <Select
                value={formData.preferred_time}
                onValueChange={(value) => setFormData(prev => ({ ...prev, preferred_time: value }))}
              >
                <SelectTrigger className="bg-transparent border-zinc-800 mt-2" data-testid="request-time">
                  <SelectValue placeholder="Seleccionar" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {timeSlots.map((time) => (
                    <SelectItem key={time} value={time}>{time}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">Describe tu idea</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Cuéntale al artista qué tipo de tatuaje quieres, tamaño, ubicación en el cuerpo, referencias, etc."
              className="bg-transparent border-zinc-800 mt-2 min-h-[120px]"
              data-testid="request-description"
            />
          </div>

          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full bg-zinc-100 text-zinc-950 hover:bg-white uppercase tracking-widest text-xs font-bold py-4"
            data-testid="send-request-btn"
          >
            {submitting ? (
              'Enviando...'
            ) : (
              <>
                <Send size={16} className="mr-2" />
                Enviar Solicitud
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RequestAppointmentButton;
